import { Category } from '@@partner:types:app'
import React, { useCallback, useEffect, useState } from 'react'

import { useCategoriesFetcher } from '@@partner:graphql/query/catalog/categoriesWithParent.gql'
import { useMutateBulkUpdateCategoriesOrder } from '@@partner:graphql/mutation/catalog/bulkUpdateCategoriesOrder'

import {
  arrayMove,
  verticalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable'
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core'

import { Button } from 'rsuite'
import SubcategoryItem from './SubcategoryItem'

import { notifyOk } from '@@partner:helpers/notification'

type CategoriesListProps = {
  parentCategory: Category | null
  level: number
  title: string
  onCategorySelect?: (category: Category) => void
  selectedCategory?: Category | null
}

export default function CategoriesList ({ 
  parentCategory, 
  level, 
  title,
  onCategorySelect,
  selectedCategory
}: CategoriesListProps) {
  const [ data, setData ] = useState<Category[]>([])
  const [ loading, setLoading ] = useState(false)

  const { fetchCategories } = useCategoriesFetcher()
  const [ bulkUpdateCategories ] = useMutateBulkUpdateCategoriesOrder()

  useEffect(() => {
    if (!parentCategory) {
      setData([])
      return
    }

    const loadSubcategories = async () => {
      setLoading(true)
      try {
        const result = await fetchCategories({ 
          parentId: parentCategory.id,
          skipIds: [],
          style: 'orig_' 
        })
        
        const subcategories = result.categories.edges.filter(
          (cat: Category) => cat.parent_id == parentCategory.id
        )
        
        const sortedSubcategories = [ ...subcategories ].sort((a, b) => {
          return (b.customer_order || 0) - (a.customer_order || 0)
        })
        
        setData(sortedSubcategories)
      }
      catch (error) {
        console.error('Error loading subcategories:', error)
        setData([])
      }
      finally {
        setLoading(false)
      }
    }

    loadSubcategories()
  }, [ parentCategory, fetchCategories ])

  const handleEditSubcategories = useCallback((newSubcategories: Category[]) => {
    const subcategoriesWithOrder = newSubcategories.map((category, index) => ({
      ...category,
      customer_order: (newSubcategories.length - index) * 10
    }))
    setData(subcategoriesWithOrder)
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event

    if (active.id !== over?.id && data.length > 0) {
      const oldIndex = data.findIndex((i) => i.id === active.id)
      const newIndex = data.findIndex((i) => i.id === over?.id)
      const newBlocks = arrayMove(data, oldIndex, newIndex)
      handleEditSubcategories(newBlocks)
    }
  }, [ data, handleEditSubcategories ])

  const handleSubcategoryChange = useCallback((category: Category) => {
    const newSubcategories = data.map((cat) => {
      if (cat.id === category.id) {
        return category
      }
      return cat
    })
    handleEditSubcategories(newSubcategories)
  }, [ data, handleEditSubcategories ])

  const handleSubcategorySelect = useCallback((category: Category) => {
    if (onCategorySelect) {
      onCategorySelect(category)
    }
  }, [ onCategorySelect ])

  const handleUpdateSubcategories = useCallback(async () => {
    if (data.length === 0) return

    const toSave = data.map((category) => ({
      id: category.id,
      customer_order: category.customer_order,
      preview_size: category.preview_size,
      icon_id: category.icon_id,
      alternative_icon_id: category.alternative_icon_id,
      mini_icon_id: category.mini_icon_id
    }))

    const { data: saveData } = await bulkUpdateCategories({
      variables: {
        categoriesInfo: toSave
      }
    })

    if (saveData) {
      notifyOk(`Порядок ${title.toLowerCase()} сохранен`)
    }
  }, [ data, bulkUpdateCategories, title ])

  if (!parentCategory) {
    return (
      <div className={`CategoriesList CategoriesList--level-${level} CategoriesList--empty`}>
        <p>Выберите категорию для просмотра {title.toLowerCase()}</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className={`CategoriesList CategoriesList--level-${level} CategoriesList--loading`}>
        <p>Загрузка {title.toLowerCase()}...</p>
      </div>
    )
  }

  return (
    <div className={`CategoriesList CategoriesList--level-${level}`}>
      <div className="CategoriesList__header">
        <h3 className="CategoriesList__title">
          {title}: {parentCategory.name}
        </h3>
        {data.length > 0 && (
          <Button
            appearance="primary"
            onClick={handleUpdateSubcategories}
            size="sm"
          >
            Сохранить
          </Button>
        )}
      </div>
      
      {data.length === 0 ? (
        <div className="CategoriesList__empty">
          <p>Нет {title.toLowerCase()}</p>
        </div>
      ) : (
        <div className="CategoriesList__content">
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={data.map((category) => category.id)}
              strategy={verticalListSortingStrategy}
            >
              { data.map((category) => {
                return (
                  <SubcategoryItem 
                    key={category.id} 
                    category={category}
                    onChange={handleSubcategoryChange}
                    isSelected={selectedCategory?.id === category.id}
                    onSelect={onCategorySelect ? handleSubcategorySelect : undefined}
                    level={level}
                  />
                )
              }) }
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  )
}
